import { type Editor, Extension, isTextSelection } from "@tiptap/core";

import { isParagraphNode } from "commonComponents/btWrappers/editor/core/editor.utils";

export type EditorControlMode = "inlineControlsOnly" | "allControls";

export interface IOverrideTopLevelEnterKeyOptions {
    /**
     * This handler gets fired in place of the "new paragraph" command/behavior.
     *
     * **IMPORTANT:** THIS FUNCTION REFERENCE MUST BE STATICALLY BOUND TO THE EDITOR'S LIFETIME.
     * This is because tiptap does not update or rerender when extension configurations are updated
     */
    onEnterStaticHandler?: (editor: Editor) => void;
    /**
     * This handler gets fired in place of the "new paragraph" command/behavior.
     *
     * **IMPORTANT:** THIS FUNCTION REFERENCE MUST BE STATICALLY BOUND TO THE EDITOR'S LIFETIME.
     * This is because tiptap does not update or rerender when extension configurations are updated
     */
    onShiftEnterStaticHandler?: (editor: Editor) => void;
}

const isCursorWithinTopLevelParagraph = (editor: Editor) => {
    const { selection } = editor.state;

    return (
        isTextSelection(selection) &&
        selection.$cursor?.parent &&
        isParagraphNode(selection.$cursor.parent) &&
        selection.$cursor?.depth === 1
    );
};

export const OverrideTopLevelEnterKeyExtension = Extension.create<IOverrideTopLevelEnterKeyOptions>(
    {
        name: "overrideTopLevelEnterKey",

        addKeyboardShortcuts() {
            return {
                Enter: ({ editor }) => {
                    // Only disable new paragraphs, but if you're focused on a node, allow the enter
                    // button to be handled by other extensions
                    if (isCursorWithinTopLevelParagraph(editor)) {
                        this.options.onEnterStaticHandler?.(editor);
                        return true;
                    }
                    return false;
                },
                "Shift-Enter": ({ editor }) => {
                    // Only disable new paragraphs, but if you're focused on a node, shift+enter
                    // to be handled by other extensions
                    if (isCursorWithinTopLevelParagraph(editor)) {
                        this.options.onShiftEnterStaticHandler?.(editor);
                        return true;
                    }
                    return false;
                },
            };
        },
    },
);

export default OverrideTopLevelEnterKeyExtension;
